import React from "react";
import { useNavigate } from "react-router-dom";
import { User, Crown, Calendar, Mail, LogOut } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";

export default function Profile() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const initials = user.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="profile-page">
      <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-white mb-8">
        {t("profileTitle")}
      </h1>

      {/* Avatar */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-24 h-24 rounded-full bg-[#1C1917] border-2 border-amber-500/30 flex items-center justify-center mb-4">
          <span className="text-3xl font-semibold text-amber-500">{initials}</span>
        </div>
        <h2 className="text-xl font-medium text-white">{user.name}</h2>
        <p className="text-sm text-stone-500">{user.email}</p>
      </div>

      {/* Info Cards */}
      <div className="space-y-4">
        <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Crown className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">{t("accountType")}</p>
            <p className="text-white font-medium">
              {user.is_premium ? t("premium") : t("free")}
            </p>
          </div>
        </div>

        {user.is_premium && user.premium_until && (
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">{t("premiumUntil")}</p>
              <p className="text-white font-medium">
                {new Date(user.premium_until).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}

        <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">{t("email")}</p>
            <p className="text-white font-medium">{user.email}</p>
          </div>
        </div>

        <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">{t("memberSince")}</p>
            <p className="text-white font-medium">
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
            </p>
          </div>
        </div>

        {!user.is_premium && (
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
              <User className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">{t("freeRecipesUsed")}</p>
              <p className="text-white font-medium">{user.free_recipes_used} / 1</p>
            </div>
          </div>
        )}
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        data-testid="profile-logout"
        className="mt-8 w-full flex items-center justify-center gap-2 py-3 border border-red-500/20 text-red-400 rounded-full hover:bg-red-500/10 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        {t("logout")}
      </button>
    </div>
  );
}
